import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Spinner,
  Typography,
} from "@material-tailwind/react";

import AlertMessage from "../../common/actions/AlertMessage";
import InputArea from "../../common/forms/InputArea";
import InputText from "../../common/forms/InputText";
import SelectOption from "../../common/forms/SelectOption";
import Loader from "../../common/loader/Loader";
import NoContent from "../../common/shared/NoContent";
import regions from "../../common/shared/regions";
import SectionTitle from "../../common/shared/SectionTitle";
import { GRIEVANCE_DATA } from "../../constants/constants";
import {
  createGrievance,
  getUserGrievances,
  uploadGrievanceAttachment,
} from "../../redux/actions/grievance_action";

const links = [{ name: "Member Feedback & Grievances" }];

const categoryOptions = [
  { id: "court", name: "Court Issue" },
  { id: "practice", name: "Legal Practice Issue" },
  { id: "registry", name: "Registry Issue" },
  { id: "other", name: "Other" },
];

const formatLabel = (value) => value?.replaceAll("_", " ") || "Not supplied";

function Grievances() {
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formData, setFormData] = useState(GRIEVANCE_DATA);
  const [selectedFile, setSelectedFile] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { grievances, grievanceError, message, uploadMessage } = useSelector(
    (state) => state.grievance,
  );

  useEffect(() => {
    dispatch(getUserGrievances()).finally(() => setLoading(false));
  }, [dispatch]);

  const updateFormValue = ({ updateType, value }) => {
    setFormData((prev) => ({ ...prev, [updateType]: value }));
  };

  const requiresCourtName = formData.category === "court";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    const grievance = await dispatch(createGrievance(formData));

    if (grievance?.id && selectedFile) {
      const uploadData = new FormData();
      uploadData.append("file", selectedFile);
      await dispatch(uploadGrievanceAttachment(grievance.id, uploadData));
    }

    await dispatch(getUserGrievances());
    setSubmitLoading(false);

    if (grievance?.id) {
      setFormData(GRIEVANCE_DATA);
      setSelectedFile(null);
      navigate(`/home/grievances/${grievance.id}`);
    }
  };

  return (
    <>
      <SectionTitle
        title="Member Feedback & Grievances"
        description="Report problems encountered in court or in your legal practice and track follow-up updates."
        children={links}
        link="/home"
      />

      <div className="my-8 max-w-[1200px] m-auto px-4 space-y-10">
        {grievanceError && (
          <AlertMessage type="error" message={grievanceError} />
        )}
        {message && <AlertMessage type="success" message={message} />}
        {uploadMessage && (
          <AlertMessage type="success" message={uploadMessage} />
        )}

        <Card>
          <form onSubmit={handleSubmit}>
            <CardBody>
              <Typography variant="h5" color="blue-gray" className="mb-6">
                Submit a grievance
              </Typography>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SelectOption
                  labelTtile="Issue Category"
                  defaultValue={formData.category}
                  updateFormValue={updateFormValue}
                  updateType="category"
                  children={categoryOptions}
                  required={true}
                />

                <SelectOption
                  labelTtile="Practice Region"
                  defaultValue={formData.practice_region_id}
                  updateFormValue={updateFormValue}
                  updateType="practice_region_id"
                  children={regions}
                  required={true}
                />

                <InputText
                  defaultValue={formData.title}
                  updateType="title"
                  labelTitle="Issue Title"
                  updateFormValue={updateFormValue}
                  required={true}
                />

                <InputText
                  defaultValue={formData.court_name}
                  updateType="court_name"
                  labelTitle="Specific Court"
                  updateFormValue={updateFormValue}
                  required={requiresCourtName}
                />

                <div className="w-full">
                  <Typography
                    variant="small"
                    className="mb-2 text-blue-gray-700"
                  >
                    Optional Attachment
                  </Typography>
                  <input
                    type="file"
                    className="w-full rounded-lg border border-blue-gray-100 p-3"
                    onChange={(e) =>
                      setSelectedFile(e.target.files?.[0] || null)
                    }
                  />
                  {selectedFile && (
                    <Typography variant="small" className="mt-2 text-blue-700">
                      {selectedFile.name}
                    </Typography>
                  )}
                </div>
              </div>

              <div className="mt-6">
                <InputArea
                  defaultValue={formData.description}
                  updateType="description"
                  labelTitle="Describe the issue"
                  updateFormValue={updateFormValue}
                  required={true}
                />
              </div>
            </CardBody>

            <CardFooter>
              <Button type="submit" className="flex items-center gap-3">
                {submitLoading && <Spinner className="h-4 w-4" />}
                Submit Grievance
              </Button>
            </CardFooter>
          </form>
        </Card>

        <div>
          <Typography variant="h5" color="blue-gray" className="mb-4">
            My submissions
          </Typography>

          {loading ? (
            <Loader />
          ) : grievances.length === 0 ? (
            <NoContent message="You have not submitted any grievances yet." />
          ) : (
            <div className="space-y-4">
              {grievances.map((grievance) => (
                <Card key={grievance.id}>
                  <CardBody>
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div>
                        <Typography variant="h6" color="blue-gray">
                          {grievance.title}
                        </Typography>
                        <Typography className="mt-1 text-sm text-blue-gray-700">
                          {grievance.description}
                        </Typography>
                      </div>

                      {/* <span className="inline-flex rounded-full bg-black px-3 py-1 text-xs font-semibold text-white capitalize">
                        {grievance.status.replaceAll("_", " ")}
                      </span> */}
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-black">
                      <Typography variant="small" className="capitalize">
                        <span className="font-semibold">Status:</span>{" "}
                        {grievance.status.replaceAll("_", " ")}
                      </Typography>
                      <Typography variant="small" className="capitalize">
                        <span className="font-semibold">Category:</span>{" "}
                        {formatLabel(grievance.category)}
                      </Typography>
                      <Typography variant="small">
                        <span className="font-semibold">Region:</span>{" "}
                        {grievance.practice_region_name || "Not supplied"}
                      </Typography>
                      <Typography variant="small">
                        <span className="font-semibold">Court:</span>{" "}
                        {grievance.court_name || "Not supplied"}
                      </Typography>
                      <Typography variant="small">
                        <span className="font-semibold">Submitted On:</span>{" "}
                        {new Date(grievance.createdAt).toDateString()}
                      </Typography>
                    </div>

                    <Button
                      className="mt-5"
                      color="blue-gray"
                      onClick={() =>
                        navigate(`/home/grievances/${grievance.id}`)
                      }
                    >
                      View Details
                    </Button>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Grievances;
